// Popup do catalogo de midia pro consultor mandar imagem com 1 clique.
// Aparece quando consultor clica em "+" -> "Catalogo" no ChatWindow.
// Estilo: grid de cards (imagem + titulo + descricao), busca em cima.
// Click no card → envia via Evolution sendMedia e fecha o popup.

import { useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Send, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { enviarItemCatalogoParaConversa } from "@/lib/catalogoEnvio";
import { useCatalogoMidia, type CatalogoItem } from "@/hooks/useCatalogoMidia";

interface Props {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  telefoneCliente: string;
  connectionId: string;
  onSent?: () => void;
}

export default function CatalogoPicker({
  open, onClose, conversationId, telefoneCliente, connectionId, onSent,
}: Props) {
  const { items, isLoading } = useCatalogoMidia();
  const [busca, setBusca] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState<string | null>(null);
  const [enviandoId, setEnviandoId] = useState<string | null>(null);

  const categorias = useMemo(
    () =>
      Array.from(
        new Set(items.filter((i) => i.ativo).map((i) => i.categoria).filter((c): c is string => !!c)),
      ).sort(),
    [items],
  );

  const filtrados = useMemo(() => {
    let ativos = items.filter((i) => i.ativo);
    if (categoriaFiltro) ativos = ativos.filter((i) => i.categoria === categoriaFiltro);
    if (!busca.trim()) return ativos;
    const q = busca.toLowerCase();
    return ativos.filter((i) =>
      i.titulo.toLowerCase().includes(q) ||
      (i.descricao ?? "").toLowerCase().includes(q) ||
      i.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [items, busca, categoriaFiltro]);

  async function enviarItem(item: CatalogoItem) {
    if (!conversationId || !telefoneCliente || !connectionId) {
      toast.error("Conversa sem dados completos");
      return;
    }
    setEnviandoId(item.id);
    try {
      await enviarItemCatalogoParaConversa(item, { conversationId, telefoneCliente, connectionId });
      toast.success("Enviado!", { description: item.titulo });
      onSent?.();
      onClose();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? String(e);
      toast.error("Falha ao enviar", { description: msg });
    } finally {
      setEnviandoId(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" /> Catálogo de Mídia
          </DialogTitle>
          <DialogDescription>
            Clique num card pra enviar imagem + descrição diretamente pro cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por título, descrição ou tag…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-8 h-9"
            autoFocus
          />
        </div>

        {categorias.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              className={`text-[11px] px-2 py-0.5 rounded border ${!categoriaFiltro ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}
              onClick={() => setCategoriaFiltro(null)}
            >
              todas
            </button>
            {categorias.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`text-[11px] px-2 py-0.5 rounded border ${categoriaFiltro === cat ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}
                onClick={() => setCategoriaFiltro(categoriaFiltro === cat ? null : cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtrados.length === 0 ? (
          <div className="text-center py-12 text-sm text-muted-foreground">
            {busca || categoriaFiltro
              ? "Nada encontrado nesse filtro."
              : "Nenhum item no catálogo. Cadastra em Configurações → Catálogo."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtrados.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => enviarItem(item)}
                disabled={!!enviandoId}
                className="border rounded-lg overflow-hidden text-left hover:border-primary hover:shadow-md transition disabled:opacity-50 disabled:cursor-wait group"
              >
                <div className="aspect-video bg-muted/30 relative">
                  <img src={item.imagem_url} alt={item.titulo} className="w-full h-full object-cover" />
                  {enviandoId === item.id ? (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : (
                    <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-primary text-primary-foreground rounded-full px-3 py-1.5 flex items-center gap-1 text-xs font-medium shadow-lg">
                        <Send className="h-3 w-3" /> Enviar
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-2.5 space-y-1.5">
                  <h4 className="font-medium text-sm line-clamp-1">{item.titulo}</h4>
                  {item.descricao && (
                    <p className="text-[11px] text-muted-foreground line-clamp-2">{item.descricao}</p>
                  )}
                  {item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {item.tags.slice(0, 3).map((t) => (
                        <Badge key={t} variant="outline" className="text-[9px] px-1 py-0">{t}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
